import { useState } from 'react';
import Header from '@/components/feature/Header';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import ScrollToTop from '@/components/feature/ScrollToTop';
import { mockReviews } from '@/mocks/data';

const FORM_SUBMIT_URL = 'https://readdy.ai/api/form/d9pe3rub4qjhggaepsag';

export default function AboutPage() {
  const [formState, setFormState] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle');
  const [formError, setFormError] = useState('');

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const form = e.currentTarget;
    const honeypot = (form.querySelector('input[name="website_alt"]') as HTMLInputElement)?.value?.trim();
    if (honeypot) {
      setFormState('success');
      return;
    }

    try {
      setFormState('submitting');
      const formData = new FormData(form);
      const response = await fetch(FORM_SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: new URLSearchParams(formData as unknown as Record<string, string>).toString(),
      });
      const responseText = await response.text();
      let parsed;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      if (response.ok && parsed?.code === 'OK') {
        setFormState('success');
        form.reset();
      } else {
        const serverMsg = parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
        setFormError(serverMsg || '提交失败，请稍后重试');
        setFormState('error');
      }
    } catch (err) {
      setFormError('网络错误，请检查网络后重试');
      setFormState('error');
    }
  };

  return (
    <>
      <ScrollToTop />
      <Header />

      <main className="pt-20">
        <div className="bg-background-100 py-6">
          <div className="max-w-screen-2xl mx-auto">
            <Breadcrumb
              items={[
                { label: '首页', href: '/' },
                { label: '关于我们' },
              ]}
            />
          </div>
        </div>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-xl mx-auto">
            <div className="text-center mb-12">
              <h1 className="text-2xl md:text-4xl font-bold text-foreground-900 mb-4">关于洞洞杂货铺漫画</h1>
              <p className="text-foreground-600 max-w-2xl mx-auto leading-relaxed">
                洞洞杂货铺漫画成立于2024年，是国内领先的在线免费漫画阅读平台。我们致力于为广大漫画爱好者提供最优质、最便捷的漫画阅读体验。
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-14">
              <div className="p-6 rounded-lg bg-background-100 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-primary-100 flex items-center justify-center">
                  <i className="ri-heart-line text-2xl text-primary-600"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-900 mb-2">永久免费</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  无需充值会员，无需付费解锁。全部漫画永久免费开放，让每一位读者都能畅享阅读乐趣。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-accent-100 flex items-center justify-center">
                  <i className="ri-flashlight-line text-2xl text-accent-600"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-900 mb-2">极速更新</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  热门漫画每日更新，第一时间同步最新章节。支持高清画质流畅阅读，加载迅速不卡顿。
                </p>
              </div>
              <div className="p-6 rounded-lg bg-background-100 text-center">
                <div className="w-14 h-14 mx-auto mb-4 rounded-xl bg-secondary-100 flex items-center justify-center">
                  <i className="ri-shield-check-line text-2xl text-secondary-600"></i>
                </div>
                <h3 className="text-lg font-bold text-foreground-900 mb-2">正版授权</h3>
                <p className="text-sm text-foreground-600 leading-relaxed">
                  所有漫画作品均经版权方授权发布，支持原创作者，为广大读者提供合法合规的漫画阅读渠道。
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-screen-xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">读者口碑</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {mockReviews.map((review, index) => (
                <div key={index} className="p-5 rounded-lg bg-background-50">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-9 h-9 rounded-full bg-primary-100 flex items-center justify-center">
                      <span className="text-xs font-bold text-primary-700">{review.author[0]}</span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{review.author}</p>
                      <div className="flex items-center gap-0.5 mt-0.5">
                        {Array.from({ length: 5 }).map((_, i) => (
                          <i
                            key={i}
                            className={`text-[10px] ${i < review.rating ? 'ri-star-fill text-accent-500' : 'ri-star-fill text-background-300'}`}
                          ></i>
                        ))}
                      </div>
                    </div>
                  </div>
                  <p className="text-sm text-foreground-600 leading-relaxed">{review.content}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-50">
          <div className="max-w-screen-xl mx-auto">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-6 text-center">联系我们</h2>

              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-10">
                <div className="text-center p-4 rounded-lg bg-background-100">
                  <div className="w-11 h-11 mx-auto mb-3 rounded-lg bg-primary-100 flex items-center justify-center">
                    <i className="ri-mail-line text-lg text-primary-600"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">电子邮箱</h4>
                  <p className="text-xs text-foreground-500">contact@178mh.com</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-background-100">
                  <div className="w-11 h-11 mx-auto mb-3 rounded-lg bg-accent-100 flex items-center justify-center">
                    <i className="ri-qq-line text-lg text-accent-600"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">客服QQ</h4>
                  <p className="text-xs text-foreground-500">800123456</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-background-100">
                  <div className="w-11 h-11 mx-auto mb-3 rounded-lg bg-secondary-100 flex items-center justify-center">
                    <i className="ri-time-line text-lg text-secondary-600"></i>
                  </div>
                  <h4 className="text-sm font-semibold text-foreground-900 mb-1">工作时间</h4>
                  <p className="text-xs text-foreground-500">周一至周五 9:00-18:00</p>
                </div>
              </div>

              <div className="rounded-lg bg-background-100 p-6 md:p-8">
                <h3 className="text-lg font-bold text-foreground-900 mb-6">发送留言</h3>
                <form data-readdy-form id="about-contact-form" onSubmit={handleSubmit}>
                  <input
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-foreground-700 mb-1.5">姓名</label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的姓名"
                      />
                    </div>
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-foreground-700 mb-1.5">邮箱</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                        placeholder="请输入您的邮箱"
                      />
                    </div>
                  </div>
                  <div className="mb-4">
                    <label htmlFor="subject" className="block text-sm font-medium text-foreground-700 mb-1.5">主题</label>
                    <input
                      type="text"
                      id="subject"
                      name="subject"
                      required
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors"
                      placeholder="请输入留言主题"
                    />
                  </div>
                  <div className="mb-6">
                    <label htmlFor="message" className="block text-sm font-medium text-foreground-700 mb-1.5">留言内容</label>
                    <textarea
                      id="message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      className="w-full px-4 py-2.5 rounded-lg border border-background-300 bg-background-50 text-sm text-foreground-900 focus:outline-none focus:border-primary-400 focus:ring-1 focus:ring-primary-400 transition-colors resize-none"
                      placeholder="请输入您的留言内容（不超过500字）"
                    ></textarea>
                  </div>

                  <div className="flex items-center gap-4">
                    <button
                      type="submit"
                      disabled={formState === 'submitting'}
                      className="px-6 py-2.5 rounded-full bg-primary-500 text-white text-sm font-semibold hover:bg-primary-600 disabled:opacity-60 transition-colors whitespace-nowrap cursor-pointer"
                    >
                      {formState === 'submitting' ? '提交中...' : '发送留言'}
                    </button>
                    {formState === 'success' && (
                      <span className="text-sm text-green-600 font-medium">留言发送成功！我们会尽快回复您。</span>
                    )}
                    {formState === 'error' && (
                      <span className="text-sm text-red-600 font-medium">{formError}</span>
                    )}
                  </div>
                </form>
              </div>
            </div>
          </div>
        </section>

        <section className="py-10 md:py-16 px-4 md:px-6 bg-background-100">
          <div className="max-w-screen-xl mx-auto">
            <h2 className="text-xl md:text-2xl font-bold text-foreground-900 mb-8 text-center">常见问题</h2>
            <div className="max-w-3xl mx-auto space-y-4">
              <details className="rounded-lg bg-background-50 p-5 cursor-pointer group">
                <summary className="text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                  洞洞杂货铺漫画是否需要付费？
                  <i className="ri-add-line text-lg text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-lg text-foreground-500 hidden group-open:block"></i>
                </summary>
                <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                  洞洞杂货铺漫画承诺永久免费！所有漫画章节均可免费阅读，无需充值会员，无需付费解锁。我们通过广告支持运营，为读者提供完全免费的漫画阅读体验。
                </p>
              </details>
              <details className="rounded-lg bg-background-50 p-5 cursor-pointer group">
                <summary className="text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                  漫画更新频率是怎样的？
                  <i className="ri-add-line text-lg text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-lg text-foreground-500 hidden group-open:block"></i>
                </summary>
                <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                  热门漫画保持每日或每周定期更新的节奏，具体取决于作者和版权方的更新计划。我们会在第一时间同步最新章节，读者可以通过收藏功能第一时间获取更新通知。
                </p>
              </details>
              <details className="rounded-lg bg-background-50 p-5 cursor-pointer group">
                <summary className="text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                  支持哪些设备阅读？
                  <i className="ri-add-line text-lg text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-lg text-foreground-500 hidden group-open:block"></i>
                </summary>
                <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                  我们的网站采用响应式设计，完美支持电脑、平板和手机等各类设备。无论您使用什么设备，都能获得流畅舒适的漫画阅读体验。推荐使用Chrome、Safari等主流浏览器。
                </p>
              </details>
              <details className="rounded-lg bg-background-50 p-5 cursor-pointer group">
                <summary className="text-sm font-semibold text-foreground-900 list-none flex items-center justify-between">
                  如何反馈问题或提出建议？
                  <i className="ri-add-line text-lg text-foreground-500 group-open:hidden"></i>
                  <i className="ri-subtract-line text-lg text-foreground-500 hidden group-open:block"></i>
                </summary>
                <p className="text-sm text-foreground-600 mt-3 leading-relaxed">
                  您可以通过上方的联系表单、发送邮件至contact@178mh.com，或添加客服QQ 800123456 与我们取得联系。我们非常重视每一位读者的反馈，会认真对待每一条建议。
                </p>
              </details>
            </div>
          </div>
        </section>
      </main>

      <Footer />

      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'AboutPage',
          name: '关于洞洞杂货铺漫画',
          description: '洞洞杂货铺漫画是国内领先的在线免费漫画阅读平台，致力于为广大漫画爱好者提供最优质便捷的漫画阅读体验。',
          url: 'https://www.178mh.com/about',
          isPartOf: {
            '@type': 'WebSite',
            name: '洞洞杂货铺漫画',
          },
        })}
      </script>
      <script type="application/ld+json">
        {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'FAQPage',
          mainEntity: [
            {
              '@type': 'Question',
              name: '洞洞杂货铺漫画是否需要付费？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '洞洞杂货铺漫画承诺永久免费！所有漫画章节均可免费阅读，无需充值会员。',
              },
            },
            {
              '@type': 'Question',
              name: '漫画更新频率是怎样的？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '热门漫画保持每日或每周定期更新的节奏，我们会在第一时间同步最新章节。',
              },
            },
            {
              '@type': 'Question',
              name: '支持哪些设备阅读？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '网站采用响应式设计，完美支持电脑、平板和手机等各类设备。',
              },
            },
            {
              '@type': 'Question',
              name: '如何反馈问题或提出建议？',
              acceptedAnswer: {
                '@type': 'Answer',
                text: '可以通过联系表单、发送邮件或添加客服QQ与我们取得联系。',
              },
            },
          ],
        })}
      </script>
    </>
  );
}